import { useState, useEffect } from "react";
import equal from "fast-deep-equal/es6";
import { useDebouncedCallback } from "../hooks/debounce.js";
import { isStringNullOrEmpty } from "../../utils/string.js";
import "moment-timezone";
import "react-intl";
function useAutocompleteState({
  defaultOption,
  value,
  options,
  onValueChange,
  onFilterChange,
  allowEmptyValue,
  allowFreeInputValue,
  multiple,
  isAlwaysOpen,
  useValueAsDefaultInputValue,
  mode
}) {
  const [open, setOpen] = useState(isAlwaysOpen ?? false);
  const [showInputIcon, setShowInputIcon] = useState(false);
  const emptyOption = { value: "", label: "" };
  const defaultSelectedOption = defaultOption ?? (multiple ? [emptyOption] : emptyOption);
  const [optionsInternal, setOptionsInternal] = useState(options ?? []);
  const [selectedOption, setSelectedOption] = useState(getSelectedOption(optionsInternal));
  const [inputValue, setInputValue] = useState(getInputValue(selectedOption));
  const [autocompleteState, setAutocompleteState] = useState("");
  useDebouncedCallback(inputValue, mode === "server" ? 500 : 0, (text) => {
    if (!multiple && allowFreeInputValue && text) {
      if (selectedOption?.label !== text) {
        const newValue = {
          label: text,
          value: text
        };
        setSelectedOption(newValue);
        handleValueChange(newValue);
      }
    }
    handleFilterChange(text);
  });
  useEffect(() => {
    const newSelectedOption = getSelectedOption(optionsInternal);
    setSelectedOption(newSelectedOption);
    if (!multiple) {
      setInputValue(newSelectedOption?.label ?? "");
    }
  }, [JSON.stringify(value)]);
  useEffect(() => {
    if (multiple) {
      setSelectedOption(defaultOption);
    }
  }, [JSON.stringify(defaultOption)]);
  useEffect(() => {
    setOptionsInternal(options ?? []);
    if (!multiple) {
      const newSelectedOption = getSelectedOption(options);
      if (selectedOption && !equal(defaultSelectedOption, newSelectedOption) && isStringNullOrEmpty(inputValue)) {
        const newSelectedOptionLabel = newSelectedOption?.label;
        setInputValue(newSelectedOptionLabel ?? "");
      }
      if (options?.length) {
        const firstOption = options[0];
        const isInputValueExactOption = inputValue === firstOption.value && inputValue !== selectedOption?.value;
        if (isInputValueExactOption) {
          setSelectedOption(firstOption);
          handleValueChange(firstOption);
        }
      }
    }
  }, [JSON.stringify(options?.map((o) => ({ value: o?.value, label: o?.label })))]);
  function getSelectedOption(options2) {
    let result;
    if (multiple) {
      const selectedOptions = options2?.filter((i) => value.includes(i.value));
      result = selectedOptions?.length ? selectedOptions : defaultSelectedOption;
    } else {
      result = options2?.find((i) => i.value === value) ?? defaultSelectedOption;
    }
    return result;
  }
  function getInputValue(option) {
    let inputValue2 = "";
    if (useValueAsDefaultInputValue && !multiple) {
      inputValue2 = option?.label;
    }
    return inputValue2;
  }
  function handleFilterChange(inputValue2) {
    if (open && onFilterChange) {
      onFilterChange(inputValue2);
    }
  }
  function handleInputChange(_event, value2) {
    setInputValue(value2);
  }
  function handleOpen(event) {
    setOpen(true);
    setAutocompleteState(event.type);
    setShowInputIcon(false);
  }
  function handleClose(event) {
    setOpen(false);
    setAutocompleteState(event.type);
    setShowInputIcon(true);
  }
  function handleClear(event) {
    handleAutocompleteChange(event, null);
    handleInputChange(null, "");
    setOpen(true);
  }
  function handleKeyDown(event) {
    stopEvent(event);
  }
  function handleBlur(_event) {
    setAutocompleteState(null);
  }
  function stopEvent(e) {
    e.nativeEvent.stopImmediatePropagation();
    e.stopPropagation();
  }
  function handleAutocompleteChange(_event, selected) {
    let resultValue = null;
    if (typeof selected === "string") {
      resultValue = optionsInternal.find((o) => o.label === selected);
    } else if (selected && selected.inputValue) {
      resultValue = {
        label: selected.inputValue
      };
    } else {
      resultValue = selected;
    }
    if (resultValue || allowEmptyValue) {
      handleValueChange(resultValue);
    }
    setSelectedOption(resultValue);
    if (!multiple) {
      setInputValue(resultValue?.label ?? "");
    }
    if (!isAlwaysOpen) {
      setOpen(false);
    }
  }
  function handleValueChange(value2) {
    if (onValueChange) {
      onValueChange(value2);
    }
  }
  return {
    open,
    showInputIcon,
    selectedOption,
    inputValue,
    optionsInternal,
    autocompleteState,
    handleFilterChange,
    handleInputChange,
    handleBlur,
    handleOpen,
    handleClose,
    handleClear,
    handleKeyDown,
    handleAutocompleteChange
  };
}
function useAutocompleteClientFilter() {
  function filter(options, filterString) {
    let result = options;
    if (!isStringNullOrEmpty(filterString)) {
      const regexString = filterString?.split("")?.map((s) => isNaN(Number(s)) ? `[${s.toUpperCase()}${s.toLowerCase()}]` : s)?.join("");
      const regex = new RegExp(`^${regexString}.*$`);
      result = filterOptions(options, regex) ?? [];
    }
    return result;
  }
  function filterOptions(options, regex) {
    const result = [];
    options?.forEach((option) => {
      if (matchOrMatchInSubOptions(option, regex)) {
        result.push({ ...option, subOptions: filterOptions(option.subOptions, regex) });
      }
    });
    return result?.length ? result : void 0;
  }
  function matchOrMatchInSubOptions(option, regex) {
    return !!option?.label?.match(regex) || option?.subOptions?.some((o) => matchOrMatchInSubOptions(o, regex));
  }
  return { filter };
}
export {
  useAutocompleteClientFilter,
  useAutocompleteState
};
//# sourceMappingURL=hooks.js.map
